// Updated KnowledgeManagementApp.js with authentication integration
import React, { useState, useRef, useEffect, useLayoutEffect, useCallback } from 'react';
import { Menu, User, LogOut, X } from 'lucide-react';
import {
  transcribeAudioAPI,
  testConnection,
  loadUserData,
  saveRecordingToFirestore,
  saveDocumentToFirestore,
  saveTaskToFirestore,
  saveProjectToFirestore,
  updateTaskInFirestore,
  ensureAuthenticated,
  deleteTaskFromFirestore,
  deleteRecordingFromFirestore,
  deleteDocumentFromFirestore,
  deleteProjectFromFirestore,
  updateProjectInFirestore,
  updateDocumentInFirestore,
  updateRecordingInFirestore,
  loadChatThreads,
  loadChatThreadById,
  createChatThread,
  saveChatThreadMessages,
  archiveChatThread,
  migrateLegacyChatSessionIfEmpty,
  updateChatThreadTitle,
  waitForAuthState,
  consumePendingOAuthProvider,
  exchangeGoogleDriveCodeAPI,
  exchangeMicrosoftCodeAPI
} from './services/firebase';
import ragService from './services/ragService';
import documentProcessor from './services/documentProcessor';

// Import components
import Sidebar from './components/Sidebar';
import ProfileModal from './components/ProfileModal';
import CreateProjectModal from './components/CreateProjectModal';
import DashboardTab from './components/DashboardTab';
import ChatTab from './components/ChatTab';
import TasksTab from './components/TasksTab';
import BrainTab from './components/BrainTab';
import KnowledgeCaptureTab from './components/KnowledgeCaptureTab';
import IntegrationsTab from './components/IntegrationsTab';
import DocumentsTab from './components/DocumentsTab';
import TranscriptModal from './components/TranscriptModal';
import useUtils from './hooks/useUtils';

const KnowledgeManagementApp = ({ user, onSignOut }) => {
  // Core State
  const [recordings, setRecordings] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [projects, setProjects] = useState([]);
  const [chatMessages, setChatMessages] = useState([]);
  const [activeTab, setActiveTab] = useState('dashboard');

  // Recording State
  const [isRecording, setIsRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [audioCategory, setAudioCategory] = useState('meeting');
  const [isProcessing, setIsProcessing] = useState(false);

  // Chat State
  const [chatInput, setChatInput] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [chatThreads, setChatThreads] = useState([]);
  const [activeChatThreadId, setActiveChatThreadId] = useState(null);
  const [breakingDownTaskId, setBreakingDownTaskId] = useState(null);
  const [knowledgeCaptureSubTab, setKnowledgeCaptureSubTab] = useState('meeting');

  // UI State
  const [expandedTasks, setExpandedTasks] = useState(new Set());
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedFilter, setSelectedFilter] = useState('all');
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);

  // Modal States
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [showCreateProjectModal, setShowCreateProjectModal] = useState(false);
  const [showTranscriptModal, setShowTranscriptModal] = useState(false);
  const [selectedRecording, setSelectedRecording] = useState(null);
  const [taskMenuOpen, setTaskMenuOpen] = useState(null);
  const [recordingMenuOpen, setRecordingMenuOpen] = useState(null);
  const [documentMenuOpen, setDocumentMenuOpen] = useState(null);

  // Rename States
  const [renamingTask, setRenamingTask] = useState(null);
  const [renamingDocument, setRenamingDocument] = useState(null);
  const [renamingRecording, setRenamingRecording] = useState(null);

  // Error and Auth States
  const [authError, setAuthError] = useState(null);

  // Refs
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const intervalRef = useRef(null);
  const recordingStartedAtMsRef = useRef(null);
  const fileInputRef = useRef(null);
  const chatEndRef = useRef(null);
  const messagesScrollRef = useRef(null);
  const successTimeoutRef = useRef(null);

  // Utils
  const { formatTime, getPriorityColor } = useUtils();

  const projectIdForChat = null; // all threads in workspace; per-thread project is stored on each document

  // OAuth return: ?code= &state= (and optional ?error=) after Google or Microsoft redirect
  useEffect(() => {
    if (!user?.uid) {
      return;
    }
    const params = new URLSearchParams(window.location.search);
    const oauthErr = params.get('error');
    if (oauthErr) {
      const desc = params.get('error_description');
      setAuthError(
        `Sign-in did not complete: ${desc || oauthErr}`.replace(/\+/g, ' ').slice(0, 500)
      );
      window.history.replaceState({}, '', window.location.pathname + window.location.hash);
      return;
    }
    const code = params.get('code');
    const state = params.get('state');
    if (!code || !state) {
      return;
    }
    const provider = consumePendingOAuthProvider();
    let cancelled = false;
    (async () => {
      try {
        if (provider === 'microsoft') {
          await exchangeMicrosoftCodeAPI({ code, state });
        } else if (provider === 'google') {
          await exchangeGoogleDriveCodeAPI({ code, state });
        } else {
          setAuthError(
            'Could not link the account. Open Integrations, choose Connect, and sign in from there.'
          );
          window.history.replaceState({}, '', window.location.pathname + window.location.hash);
          return;
        }
        if (cancelled) {
          return;
        }
        setActiveTab('integrations');
        setShowSuccessMessage(true);
        if (successTimeoutRef.current) {
          clearTimeout(successTimeoutRef.current);
        }
        successTimeoutRef.current = setTimeout(() => setShowSuccessMessage(false), 5000);
      } catch (e) {
        if (!cancelled) {
          setAuthError(e?.message || 'Could not complete account link');
        }
      } finally {
        if (!cancelled) {
          window.history.replaceState({}, '', window.location.pathname + window.location.hash);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [user?.uid]);

  // Load chat threads and pick / create active thread; migrate legacy single-session if needed
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!user?.uid) {
        setChatMessages([]);
        setChatThreads([]);
        setActiveChatThreadId(null);
        return;
      }
      try {
        await waitForAuthState();
        await migrateLegacyChatSessionIfEmpty(user.uid, projectIdForChat);
        const { success, threads = [] } = await loadChatThreads(user.uid, projectIdForChat);
        if (cancelled) return;
        if (!success) {
          setChatThreads([]);
          return;
        }
        if (threads.length === 0) {
          const created = await createChatThread(user.uid, projectIdForChat);
          if (cancelled || !created.success) return;
          const refreshed = await loadChatThreads(user.uid, projectIdForChat);
          if (cancelled || !refreshed.success) return;
          setChatThreads(refreshed.threads);
          setActiveChatThreadId(created.id);
          setChatMessages([]);
        } else {
          setChatThreads(threads);
          setActiveChatThreadId((prev) => (prev && threads.some((t) => t.id === prev) ? prev : threads[0].id));
        }
      } catch (e) {
        console.warn('Chat threads init failed', e);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [user?.uid]);

  // Load messages for the active thread
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!user?.uid || !activeChatThreadId) {
        if (!user?.uid) setChatMessages([]);
        return;
      }
      const r = await loadChatThreadById(activeChatThreadId);
      if (cancelled || !r.success) return;
      setChatMessages(r.messages || []);
    })();
    return () => {
      cancelled = true;
    };
  }, [user?.uid, activeChatThreadId]);

  // Keep the viewport on the latest messages (flex layout can otherwise start scrolled to the top)
  useLayoutEffect(() => {
    if (activeTab !== 'chat') return;
    const el = messagesScrollRef.current;
    if (el) {
      el.scrollTop = el.scrollHeight;
    }
  }, [chatMessages, isTyping, activeTab, activeChatThreadId]);

  // Cleanup timeouts on unmount
  useEffect(() => {
    return () => {
      if (successTimeoutRef.current) {
        clearTimeout(successTimeoutRef.current);
      }
    };
  }, []);

  // Load data on mount and when user changes
  useEffect(() => {
    const initializeApp = async () => {
      if (user) {
        await checkFirebaseConnection();
        await loadFirestoreData();
        
        // Initialize RAG system with existing data
        try {
          console.log('🔄 Initializing RAG system with existing data...');
          // This will be implemented later to embed existing documents
        } catch (error) {
          console.error('⚠️ Failed to initialize RAG system:', error);
        }
      }
    };

    initializeApp();
  }, [user]);

  const checkFirebaseConnection = async () => {
    try {
      const result = await testConnection();
      if (result.success) {
        console.log('🔥 Firebase connected successfully for user:', result.user?.email);
      } else {
        if (result.needsAuth) {
          setAuthError('Authentication required');
        }
        console.log('❌ Firebase connection failed:', result.error);
      }
    } catch (error) {
      console.log('❌ Firebase connection error:', error);
    }
  };

  const loadFirestoreData = async () => {
    try {
      console.log('📊 Loading data from Firestore...');
      const userData = await loadUserData();

      if (userData.success) {
        console.log('✅ Setting loaded data:', userData);
        setRecordings(userData.recordings || []);
        setTasks(userData.tasks || []);
        setDocuments(userData.documents || []);
        setProjects(userData.projects || []);
        setAuthError(null);
      } else {
        console.log('⚠️ Failed to load Firestore data:', userData.error);
        if (userData.needsAuth) {
          setAuthError('Please sign in to access your data');
        }
      }
    } catch (error) {
      console.error('❌ Error loading Firestore data:', error);
      setAuthError('Failed to load your data');
    }
  };

  const getStats = () => {
    const completedTasks = tasks.filter((t) => t.completed).length;
    const pendingTasks = tasks.filter((t) => !t.completed).length;
    const totalTasks = tasks.length;
    return {
      totalRecordings: recordings.length,
      pendingTasks,
      completedTasks,
      totalDocuments: documents.length,
      totalTasks,
      tasksDonePercent:
        totalTasks > 0 ? Math.round((completedTasks / totalTasks) * 100) : null
    };
  };

  const openKnowledgeCapture = useCallback((subTab = 'meeting') => {
    setKnowledgeCaptureSubTab(subTab);
    setActiveTab('knowledgeCapture');
  }, []);

  useEffect(() => {
    if (activeTab === 'record' || activeTab === 'recordings') {
      openKnowledgeCapture('meeting');
    }
  }, [activeTab, openKnowledgeCapture]);

  // Recording Functions
  const startRecording = async () => {
    if (!user) {
      setAuthError('Please sign in to record audio');
      return;
    }

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });

      const mimeType = MediaRecorder.isTypeSupported('audio/webm;codecs=opus')
        ? 'audio/webm;codecs=opus'
        : 'audio/webm';

      const mediaRecorder = new MediaRecorder(stream, { mimeType });
      const audioChunks = [];

      mediaRecorder.ondataavailable = (event) => {
        audioChunks.push(event.data);
      };

      mediaRecorder.onstop = async () => {
        const audioBlob = new Blob(audioChunks, { type: 'audio/webm' });

        if (intervalRef.current) {
          clearInterval(intervalRef.current);
          intervalRef.current = null;
        }
        const startedAt = recordingStartedAtMsRef.current;
        recordingStartedAtMsRef.current = null;
        const finalRecordingTime = startedAt ? Math.max(1, Math.round((Date.now() - startedAt) / 1000)) : recordingTime;

        setIsProcessing(true);

        try {
          const result = await transcribeAudioAPI(audioBlob, audioCategory, finalRecordingTime);

          if (result.success) {
            const newRecording = {
              id: result.recordingId || Date.now(),
              timestamp: new Date().toISOString(),
              category: audioCategory,
              duration: finalRecordingTime,
              transcript: result.transcript,
              insights: result.insights || {},
              status: 'processed',
              audioUrl: result.audioUrl
            };

            // Save to Firestore
            const saveResult = await saveRecordingToFirestore(newRecording);
            
            if (saveResult.success) {
              // Update the recording with the Firestore ID
              newRecording.id = saveResult.recordingId;
              
              // Embed recording transcription in RAG system
              try {
                const recordingForRAG = {
                  id: newRecording.id,
                  content: result.transcription,
                  title: `${audioCategory} Recording`,
                  type: 'recording',
                  source: 'transcription',
                  name: `${audioCategory} Recording`,
                };
                await ragService.embedDocument(recordingForRAG, user.uid, null);
                console.log('✅ Recording transcription embedded in RAG system');
              } catch (embedError) {
                console.error('⚠️ Failed to embed recording in RAG:', embedError);
                // Don't fail the recording if embedding fails
              }
            }

            setRecordings(prev => [newRecording, ...prev]);

            // Add tasks from insights if available
            if (result.insights && result.insights.actionPoints) {
              const newTasks = result.insights.actionPoints.map(action => ({
                id: Date.now() + Math.random(),
                text: action.text,
                priority: action.priority || 'medium',
                assignee: action.assignee || user.displayName || user.email,
                dueDate: action.dueDate,
                completed: false,
                recordingId: newRecording.id,
                recordingCategory: audioCategory,
                recordingDate: new Date().toISOString(),
                source: 'recording',
                subtasks: [],
                createdAt: new Date().toISOString()
              }));

              // Save tasks to Firestore
              for (const task of newTasks) {
                const saveResult = await saveTaskToFirestore(task);
                if (saveResult.success) {
                  // Update the task with the Firestore ID
                  task.id = saveResult.taskId;
                  
                  // Embed task in RAG system
                  try {
                    const taskForRAG = {
                      id: task.id,
                      content: task.text,
                      title: `Task: ${task.text}`,
                      type: 'task',
                      source: 'ai_generated',
                      name: task.text,
                    };
                    await ragService.embedDocument(taskForRAG, user.uid, null);
                    console.log('✅ Task embedded in RAG system');
                  } catch (embedError) {
                    console.error('⚠️ Failed to embed task in RAG:', embedError);
                    // Don't fail the task creation if embedding fails
                  }
                }
              }

              setTasks(prev => [...newTasks, ...prev]);
            }

            setShowSuccessMessage(true);
            // Clear any existing timeout
            if (successTimeoutRef.current) {
              clearTimeout(successTimeoutRef.current);
            }
            // Set new timeout using ref
            successTimeoutRef.current = setTimeout(() => {
              setShowSuccessMessage(false);
            }, 3000);
          } else {
            if (result.needsAuth) {
              setAuthError('Authentication expired. Please sign in again.');
            } else {
              throw new Error(result.error || 'Transcription failed');
            }
          }
        } catch (error) {
          console.error('Error processing recording:', error);
          setAuthError('Failed to process recording. Please try again.');
        } finally {
          setIsProcessing(false);
        }
      };

      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = audioChunks;

      mediaRecorder.start();
      setIsRecording(true);
      setRecordingTime(0);
      recordingStartedAtMsRef.current = Date.now();

      intervalRef.current = setInterval(() => {
        setRecordingTime(prev => prev + 1);
      }, 1000);

    } catch (error) {
      console.error('Error starting recording:', error);
      alert('Could not access microphone. Please check permissions.');
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      // Important: don't clear recordingStartedAtMsRef before onstop runs,
      // otherwise duration falls back to stale React state (often 0).
      mediaRecorderRef.current.stop();
      mediaRecorderRef.current.stream.getTracks().forEach(track => track.stop());
      setIsRecording(false);

      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    }
  };

  const recordTabProps = {
    isRecording,
    recordingTime,
    audioCategory,
    setAudioCategory,
    isProcessing,
    showSuccessMessage,
    startRecording,
    stopRecording,
    formatTime
  };

  // Chat Functions
  const handleChatSubmit = async () => {
    if (!chatInput.trim()) return;

    if (!user) {
      setAuthError('Please sign in to use knowledge search');
      return;
    }

    const userMessage = {
      type: 'user',
      content: chatInput,
      timestamp: new Date().toISOString()
    };

    const currentInput = chatInput;
    setChatInput('');
    const projectId = null;
    let threadId = activeChatThreadId;
    if (!threadId) {
      const c = await createChatThread(user.uid, projectIdForChat);
      if (!c.success || !c.id) {
        setAuthError('Could not start a chat. Please try again.');
        return;
      }
      threadId = c.id;
      setActiveChatThreadId(c.id);
      const list = await loadChatThreads(user.uid, projectIdForChat);
      if (list.success) setChatThreads(list.threads);
    }
    const messagesAfterUser = [...chatMessages, userMessage];
    setChatMessages(messagesAfterUser);
    setIsTyping(true);

    try {
      await saveChatThreadMessages(user.uid, threadId, messagesAfterUser);
    } catch (error) {
      console.warn('⚠️ Failed to persist user chat message:', error);
    }

    try {
      const conversationHistory = [
        ...chatMessages.map(msg => ({
          role: msg.type === 'user' ? 'user' : 'assistant',
          content: msg.content
        })),
        { role: 'user', content: currentInput }
      ];

      // Use RAG service for intelligent responses
      const result = await ragService.generateResponse(
        currentInput,
        user.uid,
        projectId,
        user.uid,
        conversationHistory
      );

      const aiMessage = {
        type: 'ai',
        content: result.response || 'I apologize, but I couldn\'t process your request at the moment.',
        timestamp: new Date().toISOString(),
        sources: result.sources || [],
        confidence: result.confidence || 'medium',
      };

      const messagesFinal = [...messagesAfterUser, aiMessage];
      setChatMessages(messagesFinal);

      try {
        await saveChatThreadMessages(user.uid, threadId, messagesFinal);
        const list = await loadChatThreads(user.uid, projectIdForChat);
        if (list.success) setChatThreads(list.threads);
      } catch (error) {
        console.warn('⚠️ Failed to persist AI chat message:', error);
      }

      if (!result.success) {
        setAuthError('Failed to generate response. Please try again.');
      }
    } catch (error) {
      console.error('Error in chat:', error);

      const aiMessage = {
        type: 'ai',
        content: 'I encountered an error processing your request. Please try again.',
        timestamp: new Date().toISOString()
      };

      const messagesFinal = [...messagesAfterUser, aiMessage];
      setChatMessages(messagesFinal);

      try {
        await saveChatThreadMessages(user.uid, threadId, messagesFinal);
      } catch (persistError) {
        console.warn('⚠️ Failed to persist error chat message:', persistError);
      }
    } finally {
      setIsTyping(false);
    }
  };

  const handleNewChat = async () => {
    if (!user?.uid) {
      setAuthError('Please sign in to use chat');
      return;
    }
    const c = await createChatThread(user.uid, projectIdForChat);
    if (!c.success || !c.id) return;
    setActiveChatThreadId(c.id);
    setChatMessages([]);
    setChatInput('');
    const list = await loadChatThreads(user.uid, projectIdForChat);
    if (list.success) setChatThreads(list.threads);
  };

  const handleSelectChat = (id) => {
    setActiveChatThreadId(id);
  };

  const handleRenameThread = async (threadId, title) => {
    if (!user?.uid) return;
    try {
      await updateChatThreadTitle(user.uid, threadId, title);
      const list = await loadChatThreads(user.uid, projectIdForChat);
      if (list.success) setChatThreads(list.threads);
    } catch (e) {
      console.error('Rename thread failed', e);
    }
  };

  const handleArchiveChat = async (threadId) => {
    if (!user?.uid) return;
    try {
      await archiveChatThread(user.uid, threadId);
      const list = await loadChatThreads(user.uid, projectIdForChat);
      if (list.success) {
        setChatThreads(list.threads);
        if (activeChatThreadId === threadId) {
          if (list.threads.length > 0) {
            setActiveChatThreadId(list.threads[0].id);
          } else {
            const c = await createChatThread(user.uid, projectIdForChat);
            if (c.success && c.id) {
              setActiveChatThreadId(c.id);
              setChatMessages([]);
              const r = await loadChatThreads(user.uid, projectIdForChat);
              if (r.success) setChatThreads(r.threads);
            } else {
              setActiveChatThreadId(null);
              setChatMessages([]);
            }
          }
        }
      }
    } catch (e) {
      console.error('Archive chat failed', e);
    }
  };

  const toggleTask = async (taskId) => {
    if (!user) {
      setAuthError('Please sign in to manage tasks');
      return;
    }

    const updatedTask = tasks.find(task => task.id === taskId);
    if (!updatedTask) return;

    const newCompletedState = !updatedTask.completed;

    // Update local state
    setTasks(prev =>
      prev.map(task =>
        task.id === taskId
          ? { ...task, completed: newCompletedState }
          : task
      )
    );

    // Update in Firestore
    try {
      await updateTaskInFirestore(taskId, { completed: newCompletedState });
    } catch (error) {
      console.error('Error updating task:', error);
      // Revert local state on error
      setTasks(prev =>
        prev.map(task =>
          task.id === taskId
            ? { ...task, completed: !newCompletedState }
            : task
        )
      );
    }
  };

  const handleFileUpload = async (event) => {
    if (!user) {
      setAuthError('Please sign in to upload documents');
      return;
    }

    const files = event.target.files;
    if (!files) return;

    Array.from(files).forEach(async (file) => {
      if (file.size > 5 * 1024 * 1024) {
        alert(`File ${file.name} is too large (${(file.size / 1024 / 1024).toFixed(1)}MB). Please use files smaller than 5MB.`);
        return;
      }

      // Check if file type is supported by our processor
      if (documentProcessor.isSupported(file.type)) {
        // Use the new document processor for supported file types
        try {
          console.log('🔄 Processing document with new processor:', file.name);
          const processResult = await documentProcessor.processDocument(file);
          
          if (!processResult.success) {
            alert(`Failed to process ${file.name}: ${processResult.error}`);
            return;
          }

          const getFileType = (file) => {
            if (file.type) {
              const typeMap = {
                'application/pdf': 'PDF Document',
                'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'Word Document',
                'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'Excel Spreadsheet',
                'application/vnd.openxmlformats-officedocument.presentationml.presentation': 'PowerPoint Presentation',
                'text/plain': 'Text File',
                'text/csv': 'CSV File',
                'text/markdown': 'Markdown File',
                'application/json': 'JSON File',
                'text/html': 'HTML File',
                'text/css': 'CSS File',
                'application/javascript': 'JavaScript File'
              };
              return typeMap[file.type] || file.type;
            }

            const extension = file.name.split('.').pop()?.toLowerCase();
            const extensionMap = {
              'pdf': 'PDF Document',
              'docx': 'Word Document',
              'xlsx': 'Excel Spreadsheet',
              'pptx': 'PowerPoint Presentation',
              'txt': 'Text File',
              'csv': 'CSV File',
              'md': 'Markdown File',
              'json': 'JSON File',
              'html': 'HTML File',
              'css': 'CSS File',
              'js': 'JavaScript File'
            };
            return extensionMap[extension] || 'Unknown File Type';
          };

          const newDoc = {
            id: Date.now() + Math.random(),
            name: file.name,
            content: processResult.text, // Use the processed text
            size: file.size,
            type: getFileType(file),
            uploadDate: new Date().toISOString(),
            metadata: processResult.metadata // Include processing metadata
          };

          try {
            // Save to Firestore
            const saveResult = await saveDocumentToFirestore(newDoc);
            if (saveResult.success) {
              // Update the document with the Firestore ID
              newDoc.id = saveResult.documentId;
              
              // Embed document in RAG system
              try {
                await ragService.embedDocument(newDoc, user.uid, null);
                console.log('✅ Document embedded in RAG system');
              } catch (embedError) {
                console.error('⚠️ Failed to embed document in RAG:', embedError);
                // Don't fail the upload if embedding fails
              }
            }
            setDocuments(prev => [newDoc, ...prev]);
          } catch (error) {
            console.error('Error adding document:', error);
            if (error.message.includes('authenticated')) {
              setAuthError('Session expired. Please sign in again.');
            } else {
              alert('Error adding document. Please try again.');
            }
          }
        } catch (error) {
          console.error('Error processing document:', error);
          alert(`Error processing ${file.name}: ${error.message}`);
        }
      } else {
        // Fallback to original method for unsupported file types
        console.log('⚠️ Using fallback method for unsupported file type:', file.type);
        
        const reader = new FileReader();
        reader.onload = async (e) => {
          const getFileType = (file) => {
            if (file.type) {
              const typeMap = {
                'application/pdf': 'PDF Document',
                'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'Word Document',
                'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'Excel Spreadsheet',
                'application/vnd.openxmlformats-officedocument.presentationml.presentation': 'PowerPoint Presentation',
                'text/plain': 'Text File',
                'text/markdown': 'Markdown File',
                'application/json': 'JSON File',
                'text/html': 'HTML File',
                'text/css': 'CSS File',
                'application/javascript': 'JavaScript File'
              };
              return typeMap[file.type] || file.type;
            }

            const extension = file.name.split('.').pop()?.toLowerCase();
            const extensionMap = {
              'pdf': 'PDF Document',
              'docx': 'Word Document',
              'xlsx': 'Excel Spreadsheet',
              'pptx': 'PowerPoint Presentation',
              'txt': 'Text File',
              'md': 'Markdown File',
              'json': 'JSON File',
              'html': 'HTML File',
              'css': 'CSS File',
              'js': 'JavaScript File'
            };
            return extensionMap[extension] || 'Unknown File Type';
          };

          const newDoc = {
            id: Date.now() + Math.random(),
            name: file.name,
            content: typeof e.target.result === 'string' ? e.target.result : 'Binary file content',
            size: file.size,
            type: getFileType(file),
            uploadDate: new Date().toISOString()
          };

          try {
            // Save to Firestore
            const saveResult = await saveDocumentToFirestore(newDoc);
            if (saveResult.success) {
              // Update the document with the Firestore ID
              newDoc.id = saveResult.documentId;
              
              // Embed document in RAG system
              try {
                await ragService.embedDocument(newDoc, user.uid, null);
                console.log('✅ Document embedded in RAG system');
              } catch (embedError) {
                console.error('⚠️ Failed to embed document in RAG:', embedError);
                // Don't fail the upload if embedding fails
              }
            }
            setDocuments(prev => [newDoc, ...prev]);
          } catch (error) {
            console.error('Error adding document:', error);
            if (error.message.includes('authenticated')) {
              setAuthError('Session expired. Please sign in again.');
            } else {
              alert('Error adding document. Please try again.');
            }
          }
        };

        if (file.type.startsWith('text/') || file.name.endsWith('.txt') || file.name.endsWith('.md') || file.name.endsWith('.json') || file.name.endsWith('.html') || file.name.endsWith('.css') || file.name.endsWith('.js')) {
          reader.readAsText(file);
        } else {
          reader.readAsDataURL(file);
        }
      }
    });
  };

  // Project functions
  const createProject = async (name, description = '') => {
    if (!user) {
      setAuthError('Please sign in to create projects');
      return;
    }

    try {
      const newProject = {
        id: Date.now(),
        name,
        description,
        createdAt: new Date().toISOString(),
        recordingCount: 0,
        documentCount: 0,
        taskCount: 0
      };

      // Save to Firestore
      const saveResult = await saveProjectToFirestore(newProject);
      if (saveResult.success) {
        // Update the project with the Firestore ID
        newProject.id = saveResult.projectId;
      }

      setProjects(prev => [newProject, ...prev]);
      setShowCreateProjectModal(false);
    } catch (error) {
      console.error('Error creating project:', error);
      if (error.message.includes('authenticated')) {
        setAuthError('Session expired. Please sign in again.');
      } else {
        setAuthError('Failed to create project. Please try again.');
      }
    }
  };

  const renameProject = async (projectId, newName) => {
    if (!user) {
      setAuthError('Please sign in to rename a project');
      return;
    }
    const n = (newName && String(newName).trim()) || '';
    if (!n) {
      setAuthError('Project name cannot be empty');
      return;
    }
    const res = await updateProjectInFirestore(projectId, { name: n });
    if (res.success) {
      setProjects((prev) => prev.map((p) => (p.id === projectId ? { ...p, name: n } : p)));
    } else {
      setAuthError(res.error || 'Could not rename project');
    }
  };

  const assignItemToProject = async (kind, itemId, nextProjectId) => {
    if (!user?.uid) {
      setAuthError('Sign in to assign items to a project');
      return;
    }
    const pid =
      !nextProjectId || nextProjectId === 'unassigned' || nextProjectId === ''
        ? null
        : nextProjectId;
    try {
      if (kind === 'document') {
        const d = documents.find((x) => x.id === itemId);
        const res = await updateDocumentInFirestore(itemId, { projectId: pid });
        if (!res.success) {
          throw new Error(res.error || 'Update failed');
        }
        setDocuments((prev) => prev.map((x) => (x.id === itemId ? { ...x, projectId: pid } : x)));
        if (d) {
          const emb = {
            id: d.id,
            content: d.content || '',
            name: d.name,
            title: d.name,
            type: 'document',
            fileType: d.type
          };
          await ragService.embedDocument(emb, user.uid, pid);
        }
      } else if (kind === 'recording') {
        const rec = recordings.find((x) => x.id === itemId);
        const res = await updateRecordingInFirestore(itemId, { projectId: pid });
        if (!res.success) {
          throw new Error(res.error || 'Update failed');
        }
        setRecordings((prev) => prev.map((x) => (x.id === itemId ? { ...x, projectId: pid } : x)));
        if (rec) {
          const emb = {
            id: rec.id,
            content: rec.transcript || '',
            name: rec.category ? `${rec.category} Recording` : 'Recording',
            type: 'recording',
            title: rec.category ? `${rec.category} Recording` : 'Recording'
          };
          await ragService.embedDocument(emb, user.uid, pid);
        }
      } else if (kind === 'task') {
        const t0 = tasks.find((x) => x.id === itemId);
        const res = await updateTaskInFirestore(itemId, { projectId: pid });
        if (!res.success) {
          throw new Error(res.error || 'Update failed');
        }
        setTasks((prev) => prev.map((x) => (x.id === itemId ? { ...x, projectId: pid } : x)));
        if (t0) {
          const emb = {
            id: t0.id,
            content: t0.text,
            text: t0.text,
            type: 'task'
          };
          await ragService.embedDocument(emb, user.uid, pid);
        }
      }
    } catch (e) {
      console.error('assignItemToProject', e);
      setAuthError(e?.message || 'Could not move item to project');
    }
  };

  const deleteProject = async (projectId) => {
    if (!user) {
      setAuthError('Please sign in to delete projects');
      return;
    }

    try {
      // Ensure user is authenticated with fresh token
      await ensureAuthenticated();
      
      // Always try to delete from Firestore if the project has a string ID
      const project = projects.find(p => p.id === projectId);
      console.log('🔍 Found project to delete:', project);
      if (project && typeof project.id === 'string') {
        console.log('🗑️ Attempting to delete project from Firestore:', projectId, 'Type:', typeof projectId);
        const result = await deleteProjectFromFirestore(projectId);
        if (!result.success) {
          console.error('❌ Failed to delete project from Firestore:', result.error);
          // Still remove from local state even if Firestore deletion fails
        } else {
          console.log('✅ Project deleted from Firestore successfully');
        }
      } else {
        console.log('⚠️ Project not found or not a string ID, skipping Firestore deletion');
      }

      // Always remove from local state
      setProjects(prev => prev.filter(project => project.id !== projectId));
    } catch (error) {
      console.error('Error deleting project:', error);
      if (error.message.includes('authenticated')) {
        setAuthError('Session expired. Please sign in again.');
      }
    }
  };

  // Task functions
  const toggleTaskExpansion = (taskId) => {
    setExpandedTasks(prev => {
      const newExpanded = new Set(prev);
      if (newExpanded.has(taskId)) {
        newExpanded.delete(taskId);
      } else {
        newExpanded.add(taskId);
      }
      return newExpanded;
    });
  };

  const deleteTask = async (taskId) => {
    if (!user) {
      setAuthError('Please sign in to delete tasks');
      return;
    }

    try {
      // Ensure user is authenticated with fresh token
      await ensureAuthenticated();
      
      // Always try to delete from Firestore if the task has a string ID
      const task = tasks.find(t => t.id === taskId);
      console.log('🔍 Found task to delete:', task);
      if (task && typeof task.id === 'string') {
        console.log('🗑️ Attempting to delete task from Firestore:', taskId, 'Type:', typeof taskId);
        const result = await deleteTaskFromFirestore(taskId);
        if (!result.success) {
          console.error('❌ Failed to delete task from Firestore:', result.error);
          // Still remove from local state even if Firestore deletion fails
        } else {
          console.log('✅ Task deleted from Firestore successfully');
        }
      } else {
        console.log('⚠️ Task not found or not a string ID, skipping Firestore deletion');
      }

      // Always remove from local state
      setTasks(prev => prev.filter(task => task.id !== taskId));
      setTaskMenuOpen(null);
    } catch (error) {
      console.error('Error deleting task:', error);
      if (error.message.includes('authenticated')) {
        setAuthError('Session expired. Please sign in again.');
      }
    }
  };

  const breakdownTask = async (taskId, taskText) => {
    if (!user) {
      setAuthError('Please sign in to break down tasks');
      return;
    }

    try {
      setBreakingDownTaskId(taskId);
      // Use the same authentication pattern that works for transcription
      const { waitForAuthState } = await import('./services/firebase');
      await waitForAuthState();
      
      const userId = user?.uid;
      if (!userId) {
        throw new Error('User must be authenticated');
      }
      
      // Force token refresh to ensure we have a valid token
      if (user) {
        await user.getIdToken(true);
      }
      
      const { httpsCallable } = await import('firebase/functions');
      const { getFunctions } = await import('firebase/functions');
      // Ensure we're using the same region as the main firebase.js
      const functions = getFunctions(undefined, 'us-central1');
      
      // Use the pre-initialized functions instance
      const breakTaskIntoSubtasks = httpsCallable(functions, 'breakTaskIntoSubtasks');
      
      const result = await breakTaskIntoSubtasks({
        taskId,
        taskText,
        userId: userId
      });

      if (result.data.success) {
        // Update local state
        setTasks(prev => prev.map(task =>
          task.id === taskId
            ? { ...task, subtasks: result.data.subtasks, canBreakDown: false }
            : task
        ));
        
        // Save subtasks to Firestore to persist them
        const task = tasks.find(t => t.id === taskId);
        if (task && typeof task.id === 'string') {
          console.log('💾 Saving subtasks to Firestore for task:', taskId);
          const updateResult = await updateTaskInFirestore(taskId, {
            subtasks: result.data.subtasks,
            canBreakDown: false
          });
          if (updateResult.success) {
            console.log('✅ Subtasks saved to Firestore successfully');
          } else {
            console.error('❌ Failed to save subtasks to Firestore:', updateResult.error);
          }
        }
        
        setTaskMenuOpen(null);
      }
    } catch (error) {
      console.error('Error breaking down task:', error);
      if (error.message.includes('authenticated')) {
        setAuthError('Session expired. Please sign in again.');
      }
    } finally {
      setBreakingDownTaskId(null);
    }
  };

  // Delete functions
  const deleteRecording = async (recordingId) => {
    if (!user) {
      setAuthError('Please sign in to delete recordings');
      return;
    }

    try {
      // Ensure user is authenticated with fresh token
      await ensureAuthenticated();
      
      // Always try to delete from Firestore if the recording has a string ID
      const recording = recordings.find(r => r.id === recordingId);
      if (recording && typeof recording.id === 'string') {
        console.log('🗑️ Attempting to delete recording from Firestore:', recordingId);
        const result = await deleteRecordingFromFirestore(recordingId);
        if (!result.success) {
          console.error('❌ Failed to delete recording from Firestore:', result.error);
          // Still remove from local state even if Firestore deletion fails
        } else {
          console.log('✅ Recording deleted from Firestore successfully');
        }
      }

      // Always remove from local state
      setRecordings(prev => prev.filter(recording => recording.id !== recordingId));
      setRecordingMenuOpen(null);
    } catch (error) {
      console.error('Error deleting recording:', error);
      if (error.message.includes('authenticated')) {
        setAuthError('Session expired. Please sign in again.');
      }
    }
  };

  const deleteDocument = async (documentId) => {
    if (!user) {
      setAuthError('Please sign in to delete documents');
      return;
    }

    try {
      // Ensure user is authenticated with fresh token
      await ensureAuthenticated();
      
      // Always try to delete from Firestore if the document has a string ID
      const document = documents.find(d => d.id === documentId);
      console.log('🔍 Found document to delete:', document);
      if (document && typeof document.id === 'string') {
        console.log('🗑️ Attempting to delete document from Firestore:', documentId, 'Type:', typeof documentId);
        const result = await deleteDocumentFromFirestore(documentId);
        if (!result.success) {
          console.error('❌ Failed to delete document from Firestore:', result.error);
          // Still remove from local state even if Firestore deletion fails
        } else {
          console.log('✅ Document deleted from Firestore successfully');
        }
      } else {
        console.log('⚠️ Document not found or not a string ID, skipping Firestore deletion');
      }

      // Always remove from local state
      setDocuments(prev => prev.filter(doc => doc.id !== documentId));
      setDocumentMenuOpen(null);
    } catch (error) {
      console.error('Error deleting document:', error);
      if (error.message.includes('authenticated')) {
        setAuthError('Session expired. Please sign in again.');
      }
    }
  };

  // Rename functions
  const renameTask = (taskId, newText) => {
    setTasks(prev => prev.map(task =>
      task.id === taskId ? { ...task, text: newText } : task
    ));
    setRenamingTask(null);
    setTaskMenuOpen(null);
  };

  const renameDocument = (documentId, newName) => {
    setDocuments(prev => prev.map(doc =>
      doc.id === documentId ? { ...doc, name: newName } : doc
    ));
    setRenamingDocument(null);
    setDocumentMenuOpen(null);
  };

  const renameRecording = (recordingId, newName) => {
    setRecordings(prev => prev.map(recording =>
      recording.id === recordingId ? { ...recording, name: newName } : recording
    ));
    setRenamingRecording(null);
    setRecordingMenuOpen(null);
  };

  const stats = getStats();

  return (
    <div className="min-h-screen bg-slate-50 flex overflow-hidden w-full">
      <Sidebar
        activeTab={activeTab}
        setActiveTab={setActiveTab}
        isSidebarOpen={isSidebarOpen}
        setIsSidebarOpen={setIsSidebarOpen}
      />

      {isSidebarOpen && (
        <div
          className="fixed inset-0 bg-black/20 backdrop-blur-sm z-40 lg:hidden"
          onClick={() => setIsSidebarOpen(false)}
        />
      )}

      <div className="flex-1 flex flex-col min-w-0 h-screen w-full">
        <header className="bg-white/90 backdrop-blur-xl border-b border-gray-200/50 sticky top-0 z-30 flex-shrink-0 w-full">
          <div className="px-4 sm:px-6 lg:px-8 py-4 w-full">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <button
                  onClick={() => setIsSidebarOpen(true)}
                  className="lg:hidden p-2 rounded-lg hover:bg-gray-100 transition-colors"
                >
                  <Menu className="w-5 h-5 text-gray-600" />
                </button>
                <div>
                  <h2 className="text-2xl font-bold text-slate-900">
                    {activeTab === 'dashboard' && 'Dashboard'}
                    {activeTab === 'chat' && 'Knowledge search'}
                    {activeTab === 'knowledgeCapture' && 'Knowledge capture'}
                    {activeTab === 'tasks' && 'Task manager'}
                    {activeTab === 'documents' && 'Documents'}
                    {activeTab === 'brain' && 'Brain'}
                    {activeTab === 'integrations' && 'Integrations'}
                    {!['dashboard', 'chat', 'knowledgeCapture', 'tasks', 'documents', 'brain', 'integrations'].includes(activeTab) &&
                      activeTab}
                  </h2>
                  <p className="text-sm text-slate-600 mt-0.5">
                    {activeTab === 'dashboard' && 'Overview of your brain'}
                    {activeTab === 'record' && 'Capture and analyze audio content'}
                    {activeTab === 'tasks' && 'AI-generated tasks from recordings and capture'}
                    {activeTab === 'recordings' && 'Your transcribed audio library'}
                    {activeTab === 'chat' && 'Search and query recordings, documents, and tasks'}
                    {activeTab === 'documents' && 'Uploaded files and documents'}
                    {activeTab === 'brain' && 'Complete knowledge base overview'}
                    {activeTab === 'knowledgeCapture' &&
                      'Meeting capture and structured expert sessions'}
                    {activeTab === 'integrations' &&
                      'Connect Google and Microsoft 365 — import into projects for AI and search'}
                  </p>
                </div>
              </div>
              <div className="flex items-center space-x-4">
                {user && (
                  <div className="flex items-center space-x-2 bg-slate-100 rounded-lg px-3 py-2">
                    <div className="w-6 h-6 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center">
                      <User className="w-3 h-3 text-white" />
                    </div>
                    <span className="text-sm font-medium text-slate-700">
                      {user.displayName || user.email?.split('@')[0]}
                    </span>
                    <button
                      onClick={onSignOut}
                      className="p-1 hover:bg-slate-200 rounded transition-colors"
                      title="Sign Out"
                    >
                      <LogOut className="w-4 h-4 text-slate-600" />
                    </button>
                  </div>
                )}

                <button
                  onClick={loadFirestoreData}
                  className="p-2 text-slate-600 hover:text-slate-900 transition-colors rounded-lg hover:bg-slate-100"
                  title="Refresh data"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                </button>

                <button
                  onClick={() => setShowProfileModal(true)}
                  className="p-2 text-slate-600 hover:text-slate-900 transition-colors rounded-lg hover:bg-slate-100"
                >
                  <User className="w-5 h-5" />
                </button>
              </div>
            </div>

            {authError && (
              <div className="mt-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded-lg text-sm flex items-center justify-between">
                <span>{authError}</span>
                <button
                  onClick={() => setAuthError(null)}
                  className="ml-2 text-red-500 hover:text-red-700"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            )}
          </div>
        </header>

        <main className="flex-1 overflow-y-auto p-6 w-full">
          {activeTab === 'dashboard' && (
            <DashboardTab
              stats={stats}
              setActiveTab={setActiveTab}
              user={user}
            />
          )}

          {activeTab === 'chat' && (
            <ChatTab
              chatMessages={chatMessages}
              chatInput={chatInput}
              setChatInput={setChatInput}
              isTyping={isTyping}
              handleChatSubmit={handleChatSubmit}
              messagesScrollRef={messagesScrollRef}
              chatEndRef={chatEndRef}
              chatThreads={chatThreads}
              activeChatThreadId={activeChatThreadId}
              onNewChat={handleNewChat}
              onSelectThread={handleSelectChat}
              onArchiveThread={handleArchiveChat}
              onRenameThread={handleRenameThread}
              projectLabel="Workspace"
            />
          )}

          {activeTab === 'tasks' && (
            <TasksTab
              tasks={tasks}
              setTasks={setTasks}
              selectedFilter={selectedFilter}
              setSelectedFilter={setSelectedFilter}
              expandedTasks={expandedTasks}
              toggleTaskExpansion={toggleTaskExpansion}
              taskMenuOpen={taskMenuOpen}
              setTaskMenuOpen={setTaskMenuOpen}
              toggleTask={toggleTask}
              breakdownTask={breakdownTask}
              deleteTask={deleteTask}
              getPriorityColor={getPriorityColor}
              breakingDownTaskId={breakingDownTaskId}
              setActiveTab={setActiveTab}
              renamingTask={renamingTask}
              setRenamingTask={setRenamingTask}
              renameTask={renameTask}
              projects={projects}
              onAssignItemProject={assignItemToProject}
            />
          )}

          {activeTab === 'brain' && (
            <BrainTab
              projects={projects}
              recordings={recordings}
              documents={documents}
              tasks={tasks}
              setShowCreateProjectModal={setShowCreateProjectModal}
              onAssignItemProject={assignItemToProject}
              onRenameProject={renameProject}
              deleteProject={deleteProject}
            />
          )}

          {activeTab === 'knowledgeCapture' && (
            <KnowledgeCaptureTab
              user={user}
              projects={projects}
              initialSubTab={knowledgeCaptureSubTab}
              recordProps={recordTabProps}
              recordingsProps={{
                recordings,
                searchQuery,
                setSearchQuery,
                recordingMenuOpen,
                setRecordingMenuOpen,
                deleteRecording,
                formatTime,
                setActiveTab,
                renamingRecording,
                setRenamingRecording,
                renameRecording,
                setShowTranscriptModal,
                setSelectedRecording,
                projects,
                onAssignItemProject: assignItemToProject
              }}
              onDocumentCreated={(doc) => setDocuments((prev) => [doc, ...prev])}
            />
          )}

          {activeTab === 'documents' && (
            <DocumentsTab
              documents={documents}
              documentMenuOpen={documentMenuOpen}
              setDocumentMenuOpen={setDocumentMenuOpen}
              deleteDocument={deleteDocument}
              fileInputRef={fileInputRef}
              handleFileUpload={handleFileUpload}
              renamingDocument={renamingDocument}
              setRenamingDocument={setRenamingDocument}
              renameDocument={renameDocument}
              projects={projects}
              onAssignItemProject={assignItemToProject}
            />
          )}

          {activeTab === 'integrations' && <IntegrationsTab user={user} projects={projects} />}
        </main>
      </div>

      {/* Modals */}
      <ProfileModal
        showProfileModal={showProfileModal}
        setShowProfileModal={setShowProfileModal}
        user={user}
        onSignOut={onSignOut}
      />
      <CreateProjectModal
        showCreateProjectModal={showCreateProjectModal}
        setShowCreateProjectModal={setShowCreateProjectModal}
        createProject={createProject}
      />
      <TranscriptModal
        showModal={showTranscriptModal}
        setShowModal={setShowTranscriptModal}
        recording={selectedRecording}
      />
    </div>
  );
};

export default KnowledgeManagementApp;